import React from 'react';
import { User } from './user';
import { Abilities } from './abilities';

/**
 * Available member roles.
 */
export type MemberRole = 'SDX' | 'MEMBER';

/**
 * Available member functions.
 */
export type MemberFunction = 'DEFAULT' | 'ACCOUNT_OPERATOR' | 'ISSUER_AGENT' | 'TRADER' | 'SDX_OPERATOR';

export interface AuthMember {
  id?: string;
  sdxMemberId?: string;
  dltNodeName?: string;
  name?: string;
  roles: string[];
  assetTypes: string[];
}

export type AllRequiredMember = Required<AuthMember>;

/**
 * The authentication context contains auth related information.
 * When checking authorization logic all of this is available.
 *
 * It should provide all the required information to make a decision if a user has the rights
 * do to or access something.
 */
export interface AuthContext {
  loading: boolean;
  user?: User;
  member?: AuthMember;
  abilities?: Abilities;
  env: string;
}

/**
 * Used to remove the optional part
 */
type AllRequiredAuthContext = {
  [P in keyof AuthContext]-?: AuthContext[P];
};

export interface CanIAuthContext extends AllRequiredAuthContext {
  member: AllRequiredMember;
}

/**
 * This function is used to create the React context.
 * It is possible to provide initial values here or later by passing the data to the Context.Provider.
 */
export const createAuthContext = ({ abilities, user, loading, member, env }: { abilities?: Abilities } & AuthContext) => {
  const context = React.createContext<AuthContext>({
    abilities,
    user,
    loading,
    member,
    env,
  });
  context.displayName = 'SDX AuthContext';
  return context;
};
